if (!customElements.get('scroll-navigation')) {
  class ScrollNavigation extends HTMLElement {
    constructor() {
      super();

      this.breakpoint = 750;
      this.designMode =
        this.dataset.designMode === 'true' ||
        !!(window.Shopify && window.Shopify.designMode);

      this.list = this.querySelector('[data-scroll]');
      this.items = Array.from(
        this.querySelectorAll('.scroll-navigation__item'),
      );
      this.header = document.querySelector('.header-wrapper');
      this.announcement = document.querySelector('.announcement-bar');

      // Extra px added when scrolling to a section, to absorb sub-pixel
      // rounding errors in the offset calculation.
      this.scrollNudge = 3;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.resolveTargets();

      this.links = Array.from(
        this.querySelectorAll('.scroll-navigation__link'),
      );

      // Nothing to navigate to -> stay hidden,
      // unless we're in the editor where the bar is always shown.
      const hasTargets = this.items.some((item) => item._target);
      if (!hasTargets && !this.designMode) {
        this.style.display = 'none';
        return;
      }

      this.links.forEach((link) =>
        link.addEventListener('click', this.handleClick),
      );
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });

      this.handleScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
    }

    resolveTargets() {
      this.items.forEach((item) => {
        const link = item.querySelector('.scroll-navigation__link');
        const value = link && link.dataset.scrollTo;
        item._type =
          link && link.dataset.scrollType === 'accordion'
            ? 'accordion'
            : 'section';
        item._target = value ? this.findTarget(value, item._type) : null;
        item._link = link;

        // Hide links with no matching section/row — covers both a missing
        // section and one that renders empty (e.g. blank metafields), even
        // in the editor, since there's nothing on the page to scroll to.
        item.hidden = !item._target;
      });
    }

    // Section links match a section's Scroll ID; accordion links match the
    // handleized heading used as the <accordion-component> id.
    findTarget(value, type) {
      if (type === 'accordion') {
        const el = document.getElementById(value);
        return el && el.matches('accordion-component') ? el : null;
      }
      return document.querySelector(`[data-scroll-target="${CSS.escape(value)}"]`);
    }

    // Mirrors accordion-component.js: is-active drives the open state, and
    // only one row within a group stays open.
    openAccordion(wrapper) {
      const item = wrapper.querySelector('.accordion-component__item');
      if (!item) return;

      item.classList.add('is-active');
      Array.from(
        wrapper.querySelectorAll('.accordion-component__item'),
      ).forEach((sibling) => {
        if (sibling !== item) sibling.classList.remove('is-active');
      });
    }

    headerBottom() {
      if (!this.header) return 0;
      return Math.max(0, this.header.getBoundingClientRect().bottom);
    }

    handleScroll() {
      if (window.innerWidth >= this.breakpoint) {
        this.classList.remove('is-visible');
        return;
      }

      // Dock flush under the pinned header.
      const top = this.headerBottom();
      this.style.top = `${top}px`;

      const barHeight = this.offsetHeight;
      const line = top + barHeight;

      // Reveal once the announcement bar is scrolled out of view; hide while
      // it's still showing. With no announcement bar, reveal after 60px.
      let revealed;
      if (this.designMode) {
        revealed = true;
      } else if (this.announcement) {
        revealed = this.announcement.getBoundingClientRect().bottom <= 0;
      } else {
        revealed = window.scrollY > 60;
      }
      this.classList.toggle('is-visible', revealed);

      // Scroll-spy: active = last target whose top has crossed the bar's bottom.
      let active = null;
      this.items.forEach((item) => {
        if (!item._target) return;
        if (item._target.getBoundingClientRect().top - line <= 1) active = item;
      });

      this.items.forEach(
        (item) => item._link && item._link.classList.remove('active'),
      );
      if (active && active._link) {
        active._link.classList.add('active');
        this.centerLink(active._link);
      }
    }

    centerLink(link) {
      if (!this.list) return;
      const target =
        link.offsetLeft - (this.list.clientWidth - link.offsetWidth) / 2;
      this.list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    handleClick(event) {
      const link = event.currentTarget;
      const item = link.closest('.scroll-navigation__item');
      const target = item && item._target;
      if (!target) return;

      event.preventDefault();

      if (item._type === 'accordion') this.openAccordion(target);

      const line =
        (this.header ? this.header.offsetHeight : 0) + this.offsetHeight;
      // Nudge past the target (see scrollNudge) so the section always lands
      // under the bar despite sub-pixel rounding.
      const top =
        target.getBoundingClientRect().top +
        window.scrollY -
        line +
        this.scrollNudge;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    handleResize() {
      this.handleScroll();
    }
  }

  customElements.define('scroll-navigation', ScrollNavigation);
}
